import React, { Dispatch, SetStateAction } from 'react';
import {
  View, StyleSheet, Text, TextInput, Platform,
} from 'react-native';
import { useSelector } from 'react-redux';
import Colors from '../../../../../constants/Colors';
import Button from '../../../../Button';
import PlusIcon from '../../../../16pxIcons/PlusIcon';
import FieldItem, { ADDITIONAL_FIELD_TYPE_DETAILS } from './components/FieldItem';
import { TemplateField, UserPermission } from '../../../../../constants/Types';

interface FieldsListProps {
  fields: TemplateField[],
  setFields: Dispatch<SetStateAction<TemplateField[]>>,
  fieldRefs: TextInput[],
  fieldTypesShownIndex: number,
  setFieldTypesShownIndex: Dispatch<SetStateAction<number>>,
  scrollToEnd: () => void,
}

const FieldsList = ({
  fields, setFields, fieldRefs, fieldTypesShownIndex, setFieldTypesShownIndex, scrollToEnd,
}: FieldsListProps) => {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canEditTemplates: boolean = isAdmin
    || permissions?.includes(UserPermission.TEMPLATES_UPDATE);

  const addField: () => void = () => {
    const updatedFields: TemplateField[] = [...fields];
    updatedFields.push({
      Name: 'New additional field',
      Format: ADDITIONAL_FIELD_TYPE_DETAILS[0].Format,
      IsMandatory: true,
    });
    setFieldTypesShownIndex(null);
    setFields(updatedFields);
    scrollToEnd();
  };

  return <View style={[
    styles.container, Platform.OS === 'ios' && fieldTypesShownIndex > fields.length - 4
    && { paddingBottom: 40 * Math.max(0, (4 + fieldTypesShownIndex - fields.length)) },
  ]}>
    <Text style={[styles.initialOptionTitle, styles.subTitle]}>
      {'Additional fields for assets'}
    </Text>
    <Text style={styles.additionalFieldsDescription}>
      {'These fields are unique to the assets that are going to be part of this template and have no connection to Solve.'}
    </Text>
    {fields?.map((field: TemplateField, index: number) => <FieldItem
      {...{ index, field, fieldRefs }}
      style={{ zIndex: fields.length - index }}
      updateName={(Name: string) => setFields(fields
        .map((f: TemplateField, fieldIndex: number) => (index
          === fieldIndex ? { ...field, Name } : f)))}
      updateFormat={(Format: string) => setFields(fields
        .map((f: TemplateField, fieldIndex: number) => (
          index === fieldIndex ? { ...field, Format } : f)))}
      onFocus={() => setFieldTypesShownIndex(-1)}
      toggleMenu={() => {
        setFieldTypesShownIndex(fieldTypesShownIndex === index ? -1 : index);
        if (index > fields.length - 4) scrollToEnd();
      }}
      removeField={() => setFields(fields
        .filter((f: TemplateField, fieldIndex: number) => index !== fieldIndex))}
      typesShown={fieldTypesShownIndex === index}
      hideTypesMenu={() => setFieldTypesShownIndex(null)}
      key={`template_field_item_${index}`}
    />) ?? <View style={styles.noFieldsTextContainer}>
        <Text style={styles.noFieldsText}>{'No additional fields'}</Text>
      </View>}
    <Button
      title='Add field  '
      affirmative={false}
      onPress={addField}
      style={styles.button}
      testID='add-additional-field-btn'
      disabled={!canEditTemplates}
    >
      <PlusIcon color={Colors.light.hereworksBlue500} />
    </Button>
  </View>;
};

const styles = StyleSheet.create({
  container: {
    paddingBottom: 24,
    flexGrow: 1,
  },
  noFieldsTextContainer: {
    padding: 16,
    backgroundColor: Colors.light.hereworksWhite100,
    borderRadius: 10,
    marginBottom: 16,
    alignSelf: 'stretch',
  },
  noFieldsText: {
    fontSize: 18,
    lineHeight: 27,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack400,
  },
  button: {
    alignSelf: 'flex-start',
    flex: undefined,
  },
  initialOptionTitle: {
    fontSize: 20,
    lineHeight: 30,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
  },
  subTitle: {
    marginTop: 24,
  },
  additionalFieldsDescription: {
    fontSize: 14,
    lineHeight: 22,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack300,
    marginBottom: 16,
  },
});

export default FieldsList;
